// VsNavLower.jsx — React component that mounts into #vs-nav-lower.
//
// SPEC §2: React mounts ONLY into the #vs-nav-lower slot inside nav.sidebar.
// In standard mode: the .vs-nav__lower-standard block (Rails-rendered) is visible;
// React in this component does nothing (returns null for agent-lower).
// In agent mode: the standard lower is CSS-hidden; React renders New chat / Search / Artifacts / Chats.
//
// SPEC §7 (FROZEN event contract):
//   New chat  → window.dispatchEvent(new CustomEvent('vesta:nav:new-chat'))
//   Select    → window.dispatchEvent(new CustomEvent('vesta:nav:select-chat', { detail: { id:String } }))
//
// m-r1-08 WIRED: chat list reads chats#index (this-user human chats, newest first, titles).
//   GET /api/agent/companies/:slug/chats → [{id, title, last_active}]
//   Client-side title-substring filter ONLY — NO message-text search (V1 spec / backend contract).
//   D1: chatListView (navChatList.js) drives loading / error / empty / list states — no seed fallback.
//
// SPEC §5.4/§5.5: agent expanded = New chat + Search + Artifacts + Chats; collapsed = icons only.
// SPEC §8: localStorage keys shared with Rails.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  chatListView,
  chatCreatedEvent,
  PENDING_CHAT_ID,
  withPendingPlaceholder,
  swapPendingId,
  removeChatById,
  distinctCreators,
  chatRowBadge,
  chatRowCreatorLabel,
} from "./navChatList"
import { readCompanySlug } from "./readCompanySlug"
import { resolveShellMode } from "./modeResolver"
import { artifactModePath } from "./artifactModePath"
// Item #7 taste round (2026-07-28): the chat mirror + non-host-page navigation decision. The
// sidebar renders on every agent-chrome page, but only the activity route hosts the chat surface —
// elsewhere (the library) a chat click NAVIGATES (mirror carries which chat) instead of
// dead-dispatching, and the is-active highlight seeds from the same mirror the chat pane restores
// from, so nav and pane always agree on the open chat.
import { mirrorActiveChat, readActiveChatId, sidebarChatNavTarget } from "./libraryReturn.js"
import ChatVisibilityModal from "./ChatVisibilityModal.jsx"

function safeSessionStorage() {
  try { return window.sessionStorage } catch (_e) { return null }
}

// ── §8 localStorage key ────────────────────────────────────────────────────
const MODE_KEY     = 'vesta-agent-mode'
const COLLAPSE_KEY = 'vesta-nav-collapsed'
const CHATS_SECTION_KEY = 'vesta-agent-chats-open'

function safeGet(k) { try { return localStorage.getItem(k) } catch (_e) { return null } }
function safeSet(k, v) { try { localStorage.setItem(k, v) } catch (_e) {} }

// ── m-r1-08: fetch real chats from chats#index ───────────────────────────────
// GET /api/agent/companies/:slug/chats → { chats: [{id, title, last_active, creator}], multi_user }
// (company-wide, newest first). Returns null on error / absent slug (caller renders error state);
// otherwise { chats: [{id, title, lastActive (ms), creator}], multiUser }.
// Back-compat: tolerates a bare-array payload (pre-multi_user shape) → multiUser false.
async function fetchChats(companySlug) {
  if (!companySlug) return null
  try {
    const resp = await fetch(`/api/agent/companies/${companySlug}/chats`, {
      headers: { "Accept": "application/json" },
    })
    if (!resp.ok) return null
    const data = await resp.json()
    const rawChats = Array.isArray(data) ? data : (Array.isArray(data.chats) ? data.chats : [])
    const multiUser = Array.isArray(data) ? false : !!data.multi_user
    // Chat privacy 3e — the viewer block (id + default_chat_visibility) backs the defaults gear and
    // the per-row kebab (creator-owned rows only). Absent on the legacy bare-array payload.
    const viewer = (!Array.isArray(data) && data.viewer) || null
    return {
      // Normalize to local shape: {id, title, lastActive (ms), creator, visibility, sharedUserIds}.
      // Chat privacy 3e carries visibility (badge) + sharedUserIds (manager-only modal prefill).
      chats: rawChats.map((c) => ({
        id: String(c.id),
        title: c.title || "Untitled",
        lastActive: c.last_active ? new Date(c.last_active).getTime() : Date.now(),
        creator: c.creator || null,
        visibility: c.visibility === "private" || c.visibility === "shared" ? c.visibility : "company",
        sharedUserIds: Array.isArray(c.shared_user_ids) ? c.shared_user_ids : [],
      })),
      multiUser,
      viewer,
    }
  } catch (_e) {
    return null
  }
}

// ── §9 Lucide SVG helpers (inline, stroke-width 2) ───────────────────────────
function Icon({ name, size = 20 }) {
  const s = size
  const base = {
    width: s, height: s, viewBox: "0 0 24 24",
    fill: "none", stroke: "currentColor",
    strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round",
    "aria-hidden": "true",
  }
  const paths = {
    compose: (
      <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"/></>
    ),
    search: (
      <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>
    ),
    artifacts: (
      <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>
    ),
    chevronDown: (
      <polyline points="6 9 12 15 18 9"/>
    ),
    robot: (
      <>
        <line x1="12" y1="3" x2="12" y2="6"/>
        <circle cx="12" cy="2.5" r="1"/>
        <rect x="4.5" y="6" width="15" height="12" rx="3"/>
        <line x1="2" y1="11" x2="2" y2="14"/>
        <line x1="22" y1="11" x2="22" y2="14"/>
        <circle cx="9" cy="11" r="1.1"/>
        <circle cx="15" cy="11" r="1.1"/>
        <path d="M9.5 14.5h5"/>
      </>
    ),
    swap: (
      <>
        <polyline points="17 1 21 5 17 9"/>
        <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
        <polyline points="7 23 3 19 7 15"/>
        <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
      </>
    ),
    // Chat privacy 3e — visibility badges + the defaults gear + the per-row kebab.
    gear: (
      <>
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </>
    ),
    lock: (
      <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>
    ),
    people: (
      <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>
    ),
    kebab: (
      <><circle cx="12" cy="5" r="1.4"/><circle cx="12" cy="12" r="1.4"/><circle cx="12" cy="19" r="1.4"/></>
    ),
    // Trash (lucide trash-2) for the Delete menu item (hotfix v3.28.5).
    trash: (
      <>
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        <line x1="10" y1="11" x2="10" y2="17"/>
        <line x1="14" y1="11" x2="14" y2="17"/>
      </>
    ),
  }
  return <svg {...base}>{paths[name] || null}</svg>
}

// hotfix v3.28.5 — Rails CSRF token for mutating fetch (DELETE chat). ApplicationController runs
// `protect_from_forgery with: :null_session`, so a missing token nullifies the session and
// authenticate_user! would 401 — the same accessor AgentStudioChat.jsx uses for its POSTs.
function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || ""
}

// ── §7 Event dispatch helpers (FROZEN — m3-14 listens) ───────────────────────
function dispatchNewChat() {
  window.dispatchEvent(new CustomEvent("vesta:nav:new-chat"))
}

function dispatchSelectChat(id) {
  window.dispatchEvent(new CustomEvent("vesta:nav:select-chat", { detail: { id: String(id) } }))
}

// Chat privacy 3e — a companion event carrying the selected chat's META (title/visibility/
// sharedUserIds/creator) so the chat pane's slim header can render its visibility icon and open the
// SAME modal. Kept SEPARATE from the frozen 'vesta:nav:select-chat' {id-only} contract (m3-14 listens
// to that) — the header is a pure additive consumer of this new channel.
function dispatchChatSelected(thread) {
  if (!thread) return
  window.dispatchEvent(new CustomEvent("vesta:nav:chat-selected", {
    detail: {
      id: String(thread.id),
      title: thread.title || "",
      visibility: thread.visibility || "company",
      sharedUserIds: Array.isArray(thread.sharedUserIds) ? thread.sharedUserIds : [],
      creator: thread.creator || null,
    },
  }))
}


// ── Main component ─────────────────────────────────────────────────────────────
export default function VsNavLower() {
  // ── Mode state: driven by nav attribute + vesta:nav:mode-change event ──────
  // The server-rendered nav is authoritative: data-agent-mode is set on explicit
  // mode-aware surfaces such as /activity?mode=agent and /artifacts?mode=agent.
  // When the nav is present, its attribute decides the mode —
  // a stale localStorage MODE_KEY ("agent") must NOT leak the chat list onto a
  // Standard page. localStorage is a fallback only when the nav is absent.
  const [mode, setMode] = useState(() => {
    const nav = document.querySelector("nav.sidebar.vs-nav")
    return resolveShellMode({
      navPresent: !!nav,
      agentAttrPresent: !!(nav && nav.hasAttribute("data-agent-mode")),
      stored: safeGet(MODE_KEY),
    })
  })

  // ── Collapse state (for icon-only layout in collapsed rail) ───────────────
  // area6-nav-hover-expand.md §1/§4: `.is-collapsed` is now toggled by BOTH the
  // persisted pin state AND ephemeral hover-expand, so reading the live class (when the
  // nav is present) is authoritative for both cases — this is what lets the full New
  // chat / Search / Chats list render while hover-expanded, not just compact icons.
  // MIGRATION: the localStorage fallback (nav absent — should not happen in practice)
  // now reads COLLAPSE_KEY as a "pinned open" flag: '0' = pinned; '1'/absent =
  // collapsed-at-rest, which is also the new default. See the inline nav JS for the
  // full migration note (this key's raw string values keep their old meaning; only the
  // interpretation — and the absent-key default — changed).
  const [collapsed, setCollapsed] = useState(() => {
    const nav = document.querySelector("nav.sidebar.vs-nav")
    // When the nav is present, its class is ground truth (covers hover-expand, which
    // never touches localStorage) — read it directly rather than falling through to the
    // persisted preference, which would be stale/wrong mid-hover.
    if (nav) return nav.classList.contains("is-collapsed")
    return safeGet(COLLAPSE_KEY) !== "0"
  })

  // Listen for mode changes dispatched by the nav JS
  useEffect(() => {
    function onModeChange(e) {
      setMode(e.detail.mode)
    }
    window.addEventListener("vesta:nav:mode-change", onModeChange)
    return () => window.removeEventListener("vesta:nav:mode-change", onModeChange)
  }, [])

  // Observe nav class changes for collapse state
  useEffect(() => {
    const nav = document.querySelector("nav.sidebar.vs-nav")
    if (!nav) return
    const obs = new MutationObserver(() => {
      setCollapsed(nav.classList.contains("is-collapsed"))
    })
    obs.observe(nav, { attributes: true, attributeFilter: ["class"] })
    return () => obs.disconnect()
  }, [])

  // Also observe data-agent-mode attribute changes
  useEffect(() => {
    const nav = document.querySelector("nav.sidebar.vs-nav")
    if (!nav) return
    const obs = new MutationObserver(() => {
      const isAgent = nav.hasAttribute("data-agent-mode")
      setMode(isAgent ? "agent" : "standard")
    })
    obs.observe(nav, { attributes: true, attributeFilter: ["data-agent-mode"] })
    return () => obs.disconnect()
  }, [])

  // ── Standard mode: just show the Rails-rendered block (already in DOM) ────
  if (mode !== "agent") {
    return null // standard lower is already rendered in Rails HAML
  }

  // ── Agent mode: render New chat / Search / Artifacts / Chats ──────────────
  return (
    <AgentNavLower collapsed={collapsed} />
  )
}

// ── Agent lower section (§5.4 expanded / §5.5 collapsed) ─────────────────────
function AgentNavLower({ collapsed }) {
  const artifactsHref = artifactModePath("/artifacts", "agent")
  // AI Documents (né Artifacts) is platform-admin-only while it incubates (2026-07). The flag is
  // stamped server-side on the mount slot (data-admin) so the link never flashes for non-admins;
  // the controllers enforce the same gate for direct URLs.
  const isAdmin =
    typeof document !== "undefined" &&
    document.getElementById("vs-nav-lower")?.dataset?.admin === "true"
  // D1: real chats from chats#index — NO seed fallback. Track loading/error so
  // chatListView can render the loading spinner / error-retry / empty / list states.
  const [realChats, setRealChats] = useState(null) // null until first fetch resolves
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  // Company-wide list: whether this company has >1 member (server-computed). The creator filter
  // only renders when this is true AND the loaded chats span more than one distinct author.
  const [multiUser, setMultiUser] = useState(false)
  // Chat privacy 3e — the viewer block ({id, default_chat_visibility}) drives the defaults gear +
  // the per-row kebab (creator-owned rows only). Null until the first fetch resolves.
  const [viewer, setViewer] = useState(null)
  // Chat privacy 3e — the open sharing/visibility modal: { mode:'chat'|'defaults', chat } or null.
  const [modal, setModal] = useState(null)
  // Selected creator id for the client-side author filter ('' = all authors).
  const [creatorFilter, setCreatorFilter] = useState("")
  // The creator filter and chat history form one disclosure section. Preserve the user's
  // preference across navigation/remounts while defaulting open for first-time visitors.
  const [chatsOpen, setChatsOpen] = useState(() => safeGet(CHATS_SECTION_KEY) !== "0")
  // D3: pending placeholder — true the instant "New chat" is sent, until the
  // chat-created event swaps the __pending__ row for the real chat.
  const [pending, setPending] = useState(false)
  const companySlug = useMemo(() => readCompanySlug(), [])

  // Item #7 taste (iv): seed the is-active highlight from the SAME sessionStorage mirror the chat
  // pane restores from — a restored chat (browser back, refresh, library sidebar navigation)
  // renders highlighted, not orphaned.
  const [activeId, setActiveId] = useState(() => readActiveChatId(safeSessionStorage()))

  // hotfix v3.28.5 — per-row options menu (Delete). openMenuId = the chat id whose menu is open
  // (null = none). ONE menu is open at a time and rendered ONCE at the component root as a fixed
  // popover anchored to menuPos, so the overflow:auto chat list can't clip it.
  const [openMenuId, setOpenMenuId] = useState(null)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })

  // Fetch chats on mount (and when agent mode activates).
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(false)
    fetchChats(companySlug).then((result) => {
      if (cancelled) return
      setLoading(false)
      if (result === null) {
        setError(true)
        setRealChats([])
      } else {
        setError(false)
        setRealChats(result.chats)
        setMultiUser(result.multiUser)
        setViewer(result.viewer || null)
      }
    })
    return () => { cancelled = true }
  }, [companySlug])

  // D2 (listen half): when a chat is created (dispatched by the chat surface, a
  // separate agent), refetch the list AND optimistically select e.detail.id. The
  // refetch swaps the __pending__ placeholder for the real titled row.
  useEffect(() => {
    function onNewChatCreated(e) {
      const createdId = e && e.detail ? e.detail.id : null
      if (createdId != null) setActiveId(String(createdId)) // optimistic select
      setPending(false) // D3: the real row arrives via the refetch → drop __pending__
      fetchChats(companySlug).then((result) => {
        setLoading(false)
        if (result === null) {
          setError(true)
          setRealChats((prev) => swapPendingId(prev || [], createdId))
        } else {
          setError(false)
          setRealChats(swapPendingId(result.chats, createdId))
          setMultiUser(result.multiUser)
          setViewer(result.viewer || null)
        }
      })
    }
    window.addEventListener("vesta:nav:chat-created", onNewChatCreated)
    return () => window.removeEventListener("vesta:nav:chat-created", onNewChatCreated)
  }, [companySlug])

  // Chat privacy 3e — when a chat's visibility changes (from THIS nav's modal, the chat-header icon,
  // or the per-row kebab — all dispatch 'vesta:chat:visibility-changed'), refetch so the badges +
  // the manager-only sharedUserIds prefill stay in sync. Reuses the chat-created refetch pattern.
  useEffect(() => {
    function onVisibilityChanged() {
      fetchChats(companySlug).then((result) => {
        if (result === null) return
        setRealChats(result.chats)
        setMultiUser(result.multiUser)
        setViewer(result.viewer || null)
      })
    }
    window.addEventListener("vesta:chat:visibility-changed", onVisibilityChanged)
    return () => window.removeEventListener("vesta:chat:visibility-changed", onVisibilityChanged)
  }, [companySlug])

  // Item #7 taste (iv): programmatic selections — the shell's library-return RESTORE dispatch and
  // the panel's review-chat seed both emit the frozen id-only 'vesta:nav:select-chat' contract
  // WITHOUT going through handleSelectChat. The nav must reflect them or the restored chat renders
  // with no is-active highlight. (handleSelectChat's own dispatch also lands here — it re-sets the
  // same id, a no-op.)
  useEffect(() => {
    function onExternalSelect(e) {
      const id = e && e.detail ? e.detail.id : null
      if (id == null) return
      setPending(false)
      setActiveId(String(id))
    }
    window.addEventListener("vesta:nav:select-chat", onExternalSelect)
    return () => window.removeEventListener("vesta:nav:select-chat", onExternalSelect)
  }, [])

  // DEFECT #4: when a message is SENT to an EXISTING chat (dispatched by the chat surface),
  // optimistically bump that chat's lastActive to now so the recency-sort floats it to the
  // top immediately. This fires ONLY on send — selecting a chat does NOT dispatch this event.
  useEffect(() => {
    function onChatActivity(e) {
      const activeChatId = e && e.detail ? String(e.detail.id) : null
      if (activeChatId == null) return
      setRealChats((prev) =>
        Array.isArray(prev)
          ? prev.map((c) => (String(c.id) === activeChatId ? { ...c, lastActive: Date.now() } : c))
          : prev
      )
    }
    window.addEventListener("vesta:nav:chat-activity", onChatActivity)
    return () => window.removeEventListener("vesta:nav:chat-activity", onChatActivity)
  }, [])

  // m-r1-08: title-substring filter ONLY — NO message-text search (V1 spec / backend contract).
  const [searchQuery, setSearchQuery] = useState("")
  const [searchOpen, setSearchOpen] = useState(false)
  const searchRef = useRef(null)

  // Open search: focus input
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus()
    }
  }, [searchOpen])

  // D3: optionally prepend the pending placeholder row, then build the view-model
  // (loading / error / empty / list) via the navChatList helper. The placeholder is
  // shown immediately on "New chat" and cleared when the chat-created event fires.
  const view = useMemo(() => {
    const baseChats = pending ? withPendingPlaceholder(realChats || []) : (realChats || [])
    return chatListView({
      chats: baseChats,
      activeId: pending ? PENDING_CHAT_ID : activeId,
      search: searchQuery,
      // Don't filter by creator while the pending placeholder is showing (it has no creator) —
      // that would hide the just-started "New chat" row.
      creatorId: pending ? "" : creatorFilter,
      loading,
      error,
    })
  }, [realChats, pending, activeId, searchQuery, creatorFilter, loading, error])

  // Distinct authors across the loaded chats — the creator filter only appears when the company
  // is multi-user AND the chats actually span more than one author. If the currently-selected
  // creator disappears from the list (e.g. after a refetch), fall back to "all".
  const creators = useMemo(() => distinctCreators(realChats || []), [realChats])
  const showCreatorFilter = multiUser && creators.length > 1
  useEffect(() => {
    if (creatorFilter && !creators.some((c) => String(c.id) === String(creatorFilter))) {
      setCreatorFilter("")
    }
  }, [creators, creatorFilter])

  const handleNewChat = useCallback(() => {
    // Item #7 taste (ii): on a page WITHOUT the chat surface (the library), New chat navigates to
    // the agent shell with a FRESH session — clear the mirror so no old chat restores on landing.
    const target = sidebarChatNavTarget(window.location.pathname)
    if (target) {
      mirrorActiveChat(safeSessionStorage(), null)
      window.location.assign(target)
      return
    }
    // D3: lazy-create — NO POST /chats here. Just clear the pane and show the
    // optimistic __pending__ placeholder (active); the real row is created on first
    // message and arrives via the vesta:nav:chat-created event (D2).
    setActiveId(null)
    setSearchQuery("")
    setSearchOpen(false)
    setPending(true)
    dispatchNewChat()
  }, [])

  const handleSelectChat = useCallback((thread) => {
    // Back-compat: callers may pass either a thread object or a bare id.
    const obj = thread && typeof thread === "object" ? thread : null
    const id = obj ? obj.id : thread
    // Item #7 taste (ii): on a page WITHOUT the chat surface a dispatch lands on no listener — the
    // row looked responsive but did nothing. Navigate instead: the mirror carries WHICH chat
    // (overriding whatever chat any staged return descriptor pointed at) and the chat pane's
    // mount-time mirror restore opens it.
    const target = sidebarChatNavTarget(window.location.pathname)
    if (target) {
      mirrorActiveChat(safeSessionStorage(), String(id))
      window.location.assign(target)
      return
    }
    setPending(false)
    setActiveId(String(id))
    dispatchSelectChat(id)
    if (obj) dispatchChatSelected(obj) // Chat privacy 3e — carry meta to the pane header
  }, [])

  // hotfix v3.28.5 — open the row options menu, anchored under the kebab. The menu is a fixed
  // popover (viewport coords) so it escapes the chat list's overflow clip. Right-align it to the
  // kebab and clamp to the viewport so it never spills off-screen for the bottom-most rows.
  const MENU_WIDTH = 168
  const toggleMenu = useCallback((id, e) => {
    e.stopPropagation()
    // Read the rect NOW, not inside the updater: React nulls e.currentTarget once the handler
    // returns, and updater callbacks can run after (and in dev, twice) — reading it there crashes
    // with "Cannot read properties of null (reading 'getBoundingClientRect')".
    const r = e.currentTarget.getBoundingClientRect()
    setOpenMenuId((cur) => {
      if (cur === id) return null
      const left = Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8))
      const top = Math.min(r.bottom + 4, window.innerHeight - 60)
      setMenuPos({ top, left })
      return id
    })
  }, [])

  // Delete a chat (soft-delete server-side). OPTIMISTIC: drop the row immediately, then DELETE.
  // On failure, refetch to restore server truth so a failed delete never silently loses the row.
  // Deleting the currently-open chat resets the surface to a fresh composer (ChatGPT behavior).
  const handleDeleteChat = useCallback((id) => {
    const cid = String(id)
    setOpenMenuId(null)
    setRealChats((prev) => removeChatById(prev, cid))
    if (activeId != null && String(activeId) === cid) {
      setActiveId(null)
      dispatchNewChat()
    }
    if (!companySlug) return
    fetch(`/api/agent/companies/${companySlug}/chats/${cid}`, {
      method: "DELETE",
      headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken() },
    })
      .then((resp) => {
        if (!resp.ok) throw new Error(`delete failed: ${resp.status}`)
      })
      .catch(() => {
        // Rollback to server truth. fetchChats returns null on error → leave the optimistic
        // state as-is (best effort) rather than blanking the list.
        fetchChats(companySlug).then((result) => {
          if (result) {
            setRealChats(result.chats)
            setMultiUser(result.multiUser)
          }
        })
      })
  }, [companySlug, activeId])

  // Close the open menu on outside-click, Escape, or any scroll/resize (a fixed popover would
  // otherwise detach from its kebab once the list scrolls).
  useEffect(() => {
    if (openMenuId == null) return undefined
    function onDocDown(e) {
      if (e.target.closest && e.target.closest(".vs-nav-agent__chat-menu, .vs-nav-agent__chat-kebab")) return
      setOpenMenuId(null)
    }
    function onKey(e) { if (e.key === "Escape") setOpenMenuId(null) }
    function onScrollResize() { setOpenMenuId(null) }
    document.addEventListener("mousedown", onDocDown)
    document.addEventListener("keydown", onKey)
    window.addEventListener("resize", onScrollResize)
    window.addEventListener("scroll", onScrollResize, true) // capture: scroll doesn't bubble
    return () => {
      document.removeEventListener("mousedown", onDocDown)
      document.removeEventListener("keydown", onKey)
      window.removeEventListener("resize", onScrollResize)
      window.removeEventListener("scroll", onScrollResize, true)
    }
  }, [openMenuId])

  const handleSearchToggle = useCallback(() => {
    setSearchOpen((v) => {
      if (v) setSearchQuery("") // closing: clear query
      return !v
    })
  }, [])

  const handleChatsToggle = useCallback(() => {
    setChatsOpen((open) => {
      const next = !open
      safeSet(CHATS_SECTION_KEY, next ? "1" : "0")
      return next
    })
  }, [])

  // Collapsed agent rail: New-chat + Search + Artifacts icon buttons (§5.5)
  if (collapsed) {
    return (
      <div className="vs-nav-agent-lower">
        {/* New chat icon */}
        <button
          type="button"
          className="vs-nav-agent__new-chat"
          title="New chat"
          onClick={handleNewChat}
        >
          <Icon name="compose" size={17} />
          <span className="vs-nav__tip">New chat</span>
        </button>
        {/* Search icon */}
        <button
          type="button"
          className="vs-nav-agent__search-btn"
          title="Search"
          onClick={handleSearchToggle}
        >
          <Icon name="search" size={17} />
          <span className="vs-nav__tip">Search</span>
        </button>
        {isAdmin && (
          <a
            className="vs-nav-agent__artifacts-link"
            href={artifactsHref}
            title="AI Documents"
          >
            <Icon name="artifacts" size={17} />
            <span className="vs-nav__tip">AI Documents</span>
          </a>
        )}
      </div>
    )
  }

  // Expanded agent lower (§5.4)
  return (
    <div className="vs-nav-agent-lower">
      {/* New chat row */}
      <button
        type="button"
        className="vs-nav-agent__new-chat"
        onClick={handleNewChat}
      >
        <Icon name="compose" size={17} />
        <span className="vs-nav-agent__label">New chat</span>
      </button>

      {/* Search: when open = inline filter (replaces the Search row) */}
      {searchOpen ? (
        <div className="vs-nav-agent__search-input-wrap">
          <input
            ref={searchRef}
            type="text"
            className="vs-nav-agent__search-input"
            placeholder="Search chats…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") { setSearchOpen(false); setSearchQuery("") } }}
            aria-label="Search chats"
          />
        </div>
      ) : (
        <button
          type="button"
          className="vs-nav-agent__search-btn"
          onClick={handleSearchToggle}
        >
          <Icon name="search" size={17} />
          <span className="vs-nav-agent__label">Search</span>
        </button>
      )}

      {/* AI Documents (né Artifacts) uses the same ghost-row treatment as Standard navigation and
          stays between Search and the expandable Chats section. Admin-only while it incubates. */}
      {isAdmin && (
        <a className="vs-nav-agent__artifacts-link" href={artifactsHref}>
          <Icon name="artifacts" size={17} />
          <span className="vs-nav-agent__label">AI Documents</span>
          <span className="vs-nav__admin-badge">Admin only</span>
        </a>
      )}

      <section className={`vs-nav-agent__chats-section${chatsOpen ? " is-open" : ""}`}>
        {/* The label/chevron toggles the entire creator + chat-history region; the visibility
            settings gear remains a separate control with its own hit area. */}
        <div className="vs-nav-agent__chats-header">
          <button
            type="button"
            className="vs-nav-agent__chats-toggle"
            aria-expanded={chatsOpen}
            aria-controls="vs-nav-agent-chats-content"
            onClick={handleChatsToggle}
          >
            <span className="vs-nav-agent__chats-chevron" aria-hidden="true">
              <Icon name="chevronDown" size={14} />
            </span>
            <span className="vs-nav-agent__chats-header-label">Chats</span>
          </button>
          <button
            type="button"
            className="vs-nav-agent__defaults-gear"
            data-testid="chat-defaults-gear"
            title="Default visibility for new chats"
            aria-label="Default visibility for new chats"
            onClick={() => setModal({ mode: "defaults", chat: { defaultVisibility: (viewer && viewer.default_chat_visibility) || "private" } })}
          >
            <Icon name="gear" size={15} />
          </button>
        </div>

        <div
          id="vs-nav-agent-chats-content"
          className="vs-nav-agent__chats-content"
          aria-hidden={!chatsOpen}
        >
          {/* Creator filter (company-wide list): only when chats span multiple authors. The custom
              chevron gives the native select deliberate inset spacing on every browser. */}
          {showCreatorFilter && (
            <div className="vs-nav-agent__search-input-wrap vs-nav-agent__creator-filter">
              <select
                className="vs-nav-agent__search-input"
                value={creatorFilter}
                onChange={(e) => setCreatorFilter(e.target.value)}
                aria-label="Filter chats by creator"
                tabIndex={chatsOpen ? 0 : -1}
              >
                <option value="">All creators</option>
                {creators.map((c) => (
                  <option key={String(c.id)} value={String(c.id)}>
                    {c.name || "Unknown"}
                  </option>
                ))}
              </select>
              <span className="vs-nav-agent__creator-chevron" aria-hidden="true">
                <Icon name="chevronDown" size={14} />
              </span>
            </div>
          )}

          {/* Chat list (§6/D1) — chatListView drives loading / error / empty / list states */}
          <div className="vs-nav-agent__chats-list">
        {view.state === "loading" ? (
          <div className="vs-nav-agent__loading" role="status" aria-live="polite">
            <span className="vs-nav-agent__spinner" aria-hidden="true" />
            <span className="vs-nav-agent__loading-label">Loading chats…</span>
          </div>
        ) : view.state === "error" ? (
          <div className="vs-nav-agent__error">
            <span className="vs-nav-agent__error-label">{view.emptyLabel}</span>
          </div>
        ) : view.state === "empty" ? (
          <div className="vs-nav-agent__empty">{view.emptyLabel}</div>
        ) : (
          view.rows.map((thread) => {
            const isPending = thread.id === PENDING_CHAT_ID
            const isActive = isPending || thread.id === activeId
            const menuOpen = openMenuId === thread.id
            // UNION (hotfix v3.28.5 × chat privacy 3e): prod's structure — the row is a <button>,
            // the kebab a SIBLING button in the wrap (a button inside a button is invalid HTML) —
            // carrying our privacy affordances: the visibility badge on every row, and the kebab
            // rendered ONLY on the viewer's OWN chats (delete + re-scope are creator-only server-
            // side; a teammate row gets no dead affordance). Menu items live in the shared popover.
            const ownRow = !isPending && viewer && thread.creator && thread.creator.id != null && String(thread.creator.id) === String(viewer.id)
            // QA 2026-07-23 (lock-icon bug A): the badge is ownership-driven (chatRowBadge) — the
            // viewer's OWN chats get NO badge whatever their visibility; someone ELSE's chat gets the
            // sharing/people icon when it was actually shared with you (chats#index applies the
            // visible_to scope for non-admins). Platform admins (viewer.admin) see EVERY chat in the
            // company, so chatRowBadge skips the misleading "shared with you" badge for them and the
            // creator label carries the full name/email instead. The lock glyph is gone from the nav
            // list entirely.
            const badge = isPending ? null : chatRowBadge(thread, viewer)
            const creatorLabel = isPending ? "" : chatRowCreatorLabel(thread, viewer, multiUser)
            return (
              <div
                key={thread.id}
                className={`vs-nav-agent__chat-row-wrap${menuOpen ? " has-open-menu" : ""}`}
              >
                <button
                  type="button"
                  className={`vs-nav-agent__chat-row${isActive ? " is-active" : ""}${isPending ? " is-pending" : ""}`}
                  onClick={isPending ? undefined : () => handleSelectChat(thread)}
                  disabled={isPending}
                >
                  {badge && (
                    // The people badge marks "someone else's chat you can see" — label it by its
                    // author, not by the raw visibility value (bug A: ownership, not visibility).
                    // `--shared` keeps the existing purple accent from navbars.scss.
                    <span
                      className="vs-nav-agent__chat-badge vs-nav-agent__chat-badge--shared"
                      aria-label={thread.creator && thread.creator.name ? `Shared by ${thread.creator.name}` : "Shared with you"}
                      title={thread.creator && thread.creator.name ? `Shared by ${thread.creator.name}` : "Shared with you"}
                    >
                      <Icon name={badge} size={13} />
                    </span>
                  )}
                  <span className="vs-nav-agent__chat-title">{isPending ? "New chat" : thread.title}</span>
                  {/* Company-wide list: subtle author label so you can tell whose chat it is.
                      Reuses the age styling (same muted secondary text). Teammates: first name, only
                      when multi-user. Platform admins (2026-09-04): full name (or email) on every
                      row that isn't their own — the admin list holds every chat in the company. */}
                  {!isPending && creatorLabel && (
                    <span className="vs-nav-agent__chat-age" title={`Created by ${creatorLabel}`}>{creatorLabel}</span>
                  )}
                  {!isActive && thread.age && (
                    <span className="vs-nav-agent__chat-age">{thread.age}</span>
                  )}
                </button>
                {/* Kebab: hover-revealed (CSS) options trigger. Own rows only (see UNION note).
                    Stops propagation so it never selects the chat. */}
                {ownRow && (
                  <button
                    type="button"
                    className="vs-nav-agent__chat-kebab"
                    data-testid="chat-row-menu"
                    aria-label="Chat options"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    title="Options"
                    onClick={(e) => toggleMenu(thread.id, e)}
                  >
                    <Icon name="kebab" size={16} />
                  </button>
                )}
              </div>
            )
          })
        )}
          </div>
        </div>
      </section>

      {/* hotfix v3.28.5 — row options menu, rendered ONCE at root as a fixed popover (escapes the
          chat list's overflow clip). UNION: Sharing & visibility (chat privacy 3e) + Delete. */}
      {openMenuId != null && (
        <div
          className="vs-nav-agent__chat-menu"
          role="menu"
          style={{ position: "fixed", top: menuPos.top, left: menuPos.left }}
        >
          <button
            type="button"
            role="menuitem"
            className="vs-nav-agent__chat-menu-item"
            data-testid="chat-menu-share"
            onClick={(e) => {
              e.stopPropagation()
              const menuThread = view.state === "list" ? view.rows.find((t) => String(t.id) === String(openMenuId)) : null
              setOpenMenuId(null)
              if (menuThread) setModal({ mode: "chat", chat: menuThread })
            }}
          >
            <Icon name="people" size={15} />
            <span>Sharing & visibility</span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="vs-nav-agent__chat-menu-item is-danger"
            onClick={(e) => { e.stopPropagation(); handleDeleteChat(openMenuId) }}
          >
            <Icon name="trash" size={15} />
            <span>Delete chat</span>
          </button>
        </div>
      )}

      {/* §5.4: empty space below (D-A: chats are at TOP, nothing pinned at bottom) */}

      {/* Chat privacy 3e — the shared sharing/visibility modal (defaults gear + per-row kebab both
          open THIS instance). Renders null when closed, so at most one modal node is in the DOM. */}
      <ChatVisibilityModal
        open={!!modal}
        mode={modal ? modal.mode : "chat"}
        chat={modal ? modal.chat : null}
        companySlug={companySlug}
        onClose={() => setModal(null)}
      />
    </div>
  )
}
